import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';
import { pushCtaHover } from '../utils/dataLayer';

const Navbar = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Fetch unread notifications count
  useEffect(() => {
    if (token) {
      const fetchUnreadCount = async () => {
        try {
          const res = await api.get('/notifications');
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const userId = (user._id || user.id)?.toString();
          if (!userId) {
            setUnreadNotifications(0);
            return;
          }
          const userRole = user.role;

          // Filter notifications that are relevant to this user
          const relevantNotifications = res.data.filter(n => {
            // For admins/staff: show all notifications (they can see everything)
            // For regular users: only show isForAll or ones where they're in recipients
            if (userRole === 'admin' || userRole === 'staff') {
              return true; // Admins see all
            } else {
              return n.isForAll || (n.recipients && n.recipients.some(r => {
                const recipientId = r._id?.toString() || r?.toString() || (typeof r === 'string' ? r : null);
                return recipientId === userId;
              }));
            }
          });

          // Count unread notifications
          const unread = relevantNotifications.filter(n => {
            // If readBy is empty or undefined, it's unread
            if (!n.readBy || n.readBy.length === 0) return true;

            const hasRead = n.readBy.some(r => {
              const readUserId = r.user?._id?.toString() || r.user?.toString() || (typeof r.user === 'string' ? r.user : null);
              return readUserId === userId;
            });
            return !hasRead;
          }).length;

          setUnreadNotifications(unread);
        } catch (error) {
          // Silently fail if user doesn't have access
        }
      };
      fetchUnreadCount();
      // Refresh every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [token, location]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const user = token ? JSON.parse(localStorage.getItem('user') || '{}') : null;
  const isCustomer = user?.role === 'customer';

  const navLinks = [
    { path: '/cars', label: t.cars },
    { path: '/prices', label: language === 'en' ? 'Prices' : 'الأسعار' },
    { path: '/destinations', label: t.destinations },
    { path: '/book', label: t.bookRide },
    { path: '/reviews', label: t.reviews },
    { path: '/contact', label: t.contactUs },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled || mobileMenuOpen
          ? 'bg-blackC/95 backdrop-blur-xl border-b border-primary/10'
          : 'bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 lg:h-24">
            {/* Logo */}
            <Link to="/" className="flex flex-col group">
              <span className="text-xl text-cream group-hover:text-primary transition-colors duration-300 leading-tight" style={{ fontFamily: '"Lobster", cursive' }}>
                Global
              </span>
              <span className="text-2xl text-cream group-hover:text-primary transition-colors duration-300 leading-tight" style={{ fontFamily: '"Lobster", cursive' }}>
                Limousine
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onMouseEnter={() => pushCtaHover(`nav_${link.path.replace('/', '')}`)}
                  className={`relative text-sm font-medium tracking-wide transition-colors duration-300 ${isActive(link.path)
                    ? 'text-primary'
                    : 'text-cream/80 hover:text-primary'
                    }`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-px bg-primary" />
                  )}
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded-sm hover:bg-primary/10 transition-all duration-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {language === 'en' ? 'العربية' : 'EN'}
              </button>

              {/* Book Now Button - Desktop */}
              <Link
                to="/book"
                onMouseEnter={() => pushCtaHover('nav_book_now')}
                className="hidden lg:inline-flex btn-primary py-2 px-6 text-xs"
              >
                {t.bookNow}
              </Link>

              {/* Admin/Login/Profile */}
              {token ? (
                <div className="hidden lg:flex items-center gap-4">
                  {/* Notifications Icon */}
                  <Link
                    to="/notifications"
                    className="relative text-cream/80 hover:text-primary transition-colors"
                    title={language === 'en' ? 'Notifications' : 'الإشعارات'}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-5 px-1.5 rounded-full bg-primary text-blackC text-xs font-bold flex items-center justify-center">
                        {unreadNotifications > 99 ? '99+' : unreadNotifications}
                      </span>
                    )}
                  </Link>
                  {isCustomer ? (
                    <Link
                      to="/profile"
                      className="text-sm text-cream/80 hover:text-primary transition-colors"
                    >
                      {t.profile}
                    </Link>
                  ) : (
                    <Link
                      to="/admin"
                      className="text-sm text-cream/80 hover:text-primary transition-colors"
                    >
                      {t.admin}
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="text-sm text-cream/80 hover:text-primary transition-colors"
                  >
                    {t.logout}
                  </button>
                </div>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="hidden lg:block text-sm text-cream/80 hover:text-primary transition-colors"
                  >
                    {language === 'en' ? 'Sign Up' : 'إنشاء حساب'}
                  </Link>
                  <Link
                    to="/login"
                    className="hidden lg:block text-sm text-cream/80 hover:text-primary transition-colors"
                  >
                    {t.login}
                  </Link>
                </>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-cream"
                aria-label="Toggle menu"
              >
                <div className="w-6 h-5 relative flex flex-col justify-between">
                  <span
                    className={`block h-0.5 bg-current transform transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                      }`}
                  />
                  <span
                    className={`block h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''
                      }`}
                  />
                  <span
                    className={`block h-0.5 bg-current transform transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                      }`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
      >
        <div
          className="absolute inset-0 bg-blackC/90 backdrop-blur-xl"
          onClick={() => setMobileMenuOpen(false)}
        />
        <div
          className={`absolute top-20 left-0 right-0 bg-blackC border-b border-primary/10 transform transition-all duration-500 ${mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
            }`}
        >
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-1">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 text-lg font-display ${isActive(link.path)
                  ? 'text-primary'
                  : 'text-cream/80 hover:text-primary'
                  } transition-colors`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {link.label}
              </Link>
            ))}

            <div className="pt-6 mt-6 border-t border-primary/10 space-y-3">
              {token ? (
                <>
                  {/* Notifications Link - Mobile */}
                  <Link
                    to="/notifications"
                    className="flex items-center gap-2 py-2 text-cream/80 hover:text-primary relative"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span>{language === 'en' ? 'Notifications' : 'الإشعارات'}</span>
                    {unreadNotifications > 0 && (
                      <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-blackC text-xs font-bold flex items-center justify-center">
                        {unreadNotifications > 99 ? '99+' : unreadNotifications}
                      </span>
                    )}
                  </Link>
                  {isCustomer ? (
                    <Link
                      to="/profile"
                      className="block py-2 text-cream/80 hover:text-primary"
                    >
                      {t.profile}
                    </Link>
                  ) : (
                    <Link
                      to="/admin"
                      className="block py-2 text-cream/80 hover:text-primary"
                    >
                      {t.admin}
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="block py-2 text-cream/80 hover:text-primary"
                  >
                    {t.logout}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="block py-2 text-cream/80 hover:text-primary"
                  >
                    {language === 'en' ? 'Sign Up' : 'إنشاء حساب'}
                  </Link>
                  <Link
                    to="/login"
                    className="block py-2 text-cream/80 hover:text-primary"
                  >
                    {t.login}
                  </Link>
                </>
              )}

              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 py-2 text-primary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {language === 'en' ? 'العربية' : 'English'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;